import {
  useCreateDealDocumentMutation,
  useCreateFactorLimitMutation,
  useGetBriefDataQuery,
  useGetFactorsBranchesQuery,
  useLazyGetGeneralAgreementV2Query,
  useSignDealDocumentMutation,
} from '@/app/api';
import { checkRole, getBase64, getFetchEror } from '@/app/helpers';
import { CabinetWrapper } from '@/shared/cabinet-wrapper';
import ComponentWithLoader from '@/shared/ComponentWithLoader/ComponentWithLoader';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { FormNumericInput } from '@/shared/inputs/form-numeric-input';
import { FormTextInput } from '@/shared/inputs/form-text-input';
import { FormDatePicker } from '@/shared/inputs/form-date-picker';
import { PageTitle } from '@/shared/page-title';
import SubjectInfoTabs from '@/shared/SubjectInfoTabs/SubjectInfoTabs';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FactorAddDocument } from '@/widgets/factor-add-document';
import { useLazyGetDocQuery } from '@/entities/didox/api';
import { eimzo } from '@/entities/eimzo';
import { useAppSelector } from '@/app/hooks';
import AlertIcon from '../../app/assets/icons/alert.svg?react';
import { Currency } from '@/app/api/types';
import { CURRENCY_OPTIONS } from '@/constants';

interface FormValues {
  amount: string;
  branchId: number | null;
  generalAgreementDocumentNumber: string;
  generalAgreementDocumentDate: string | null;
  currency: Currency;
}

export const FactorLimitsAddClientPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const { tin = '' } = useParams<{ tin: string }>();
  const { profile, isUsbLog } = useAppSelector((state) => state.auth);
  const { isManager, isBranchEmploye } = checkRole(profile?.roles);
  const { data, isLoading, error } = useGetBriefDataQuery(tin, { skip: !tin });
  const { data: branches } = useGetFactorsBranchesQuery();
  const [generalAgreementFile, setGeneralAgreementFile] = useState<File | null>(
    null
  );
  const [radioValue, setRadioValue] = useState<number>(0);

  const blockedAgreementCreation = isManager || isBranchEmploye;

  const [
    isLoadingGenerateGeneralAgreement,
    setIsLoadingGenerateGeneralAgreement,
  ] = useState(false);

  const radioGroup = [
    {
      id: 0,
      label: t('common:general_agreement_not_required'),
    },
    {
      id: 1,
      label: t('common:general_agreement_exists'),
    },
    {
      id: 2,
      label: t('common:general_agreement_create'),
    },
  ];

  const { control, handleSubmit, setValue, getValues, watch } =
    useForm<FormValues>({
      defaultValues: {
        amount: '0',
        branchId: null,
        generalAgreementDocumentNumber: '',
        generalAgreementDocumentDate: null,
        currency: 'UZS',
      },
    });

  const generalAgreementDocumentNumber = watch(
    'generalAgreementDocumentNumber'
  );
  const generalAgreementDocumentDate = watch('generalAgreementDocumentDate');

  const isFieldsEmpty =
    !generalAgreementDocumentNumber || !generalAgreementDocumentDate;

  useEffect(() => {
    if (error) {
      showNotification(getFetchEror(error), 'error');
    }
  }, [error]);

  const [createLimit, { isLoading: isCreateLoading }] =
    useCreateFactorLimitMutation();
  const [getGeneralAgreement, { isLoading: isGenerateLoading }] =
    useLazyGetGeneralAgreementV2Query();
  const [createDoc] = useCreateDealDocumentMutation();
  const [signDoc] = useSignDealDocumentMutation();
  const [getDoc] = useLazyGetDocQuery();

  const handleSignDoc = async (id: string) => {
    const docJson = await getDoc(id)
      .unwrap()
      .then((res) => res.data.json);
    const signature = await eimzo.sign(
      JSON.stringify(docJson),
      profile?.tin || '',
      isUsbLog
    );
    await signDoc({ id, signature: signature }).unwrap();
    showNotification(t('document_successfully_signed'), 'success');
  };

  const handleSignGeneralAgreement = async (id: string) => {
    try {
      await handleSignDoc(id);
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  const handleSave = async (values: FormValues) => {
    if (!data) return;

    try {
      const response = await createLimit({
        buyer: data.data.briefData.tin,
        amount: Number(values.amount.replace(/\s/g, '')),
        branchId: values.branchId,
        ...(radioValue !== 0 && {
          generalAgreementDocumentNumber: values.generalAgreementDocumentNumber,
        }),
        ...(radioValue !== 0 && {
          generalAgreementDocumentDate:
            dayjs(values.generalAgreementDocumentDate).format('DD.MM.YYYY') ||
            null,
        }),
        generalAgreementState:
          radioValue === 0
            ? 'notRequired'
            : radioValue === 1
              ? 'exists'
              : 'needToCreate',
        currency: values.currency,
      }).unwrap();

      if (radioValue === 2 && generalAgreementFile) {
        try {
          const base64 = await getBase64(generalAgreementFile).then(
            (res) => res.split(',')[1]
          );
          const docResponse = await createDoc({
            id: response.data.unique,
            base64: base64,
            date: dayjs().format('YYYY.MM.DD'),
            type: 'generalAgreement',
            number: generalAgreementFile.name,
          }).unwrap();

          const didoxIdToSign =
            docResponse.data.didoxId ||
            response.data.generalAgreement?.didoxId ||
            '';
          if (didoxIdToSign) {
            await handleSignGeneralAgreement(didoxIdToSign);
          }
        } catch (error) {
          showNotification(getFetchEror(error), 'error');
        }
      }

      navigate('/factor/limits');
      showNotification(t('client_added_successfully'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  const handleGenerateGeneralAgreement = async () => {
    try {
      setIsLoadingGenerateGeneralAgreement(true);
      const response = await getGeneralAgreement({
        buyer: data?.data.briefData.tin || '',
        amount: Number(getValues('amount').replace(/\s/g, '')),
        branchId: getValues('branchId') || null,
        generalAgreementDocumentNumber:
          getValues('generalAgreementDocumentNumber') || '',
        generalAgreementDocumentDate:
          dayjs(getValues('generalAgreementDocumentDate')).format(
            'DD.MM.YYYY'
          ) || null,
      });

      const base64String = response?.data?.base64 || '';

      if (base64String) {
        const binaryString = atob(base64String);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const file = new File([blob], 'Генеральное_соглашение.pdf', {
          type: 'application/pdf',
        });
        setGeneralAgreementFile(file);
      }
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    } finally {
      setIsLoadingGenerateGeneralAgreement(false);
    }
  };

  return (
    <CabinetWrapper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pb: 0,
      }}
    >
      <ComponentWithLoader isLoading={isLoading}>
        <PageTitle title={data?.data.briefData.name || ''} />
        <Stack direction='column' spacing={2} mt={2}>
          <Typography fontSize={17} fontWeight={600}>
            {t('conditions_of_limit')}
          </Typography>
          <Stack direction='row' spacing={2}>
            <FormControl fullWidth>
              <UIFormLabel>{t('limit_sum')}:</UIFormLabel>
              <FormNumericInput
                control={control}
                name='amount'
                rules={{ required: true }}
                thousandSeparator=' '
              />
            </FormControl>

            <FormControl fullWidth>
              <UIFormLabel>{t('currency')}:</UIFormLabel>
              <Controller
                control={control}
                name='currency'
                render={({ field }) => (
                  <UIAutocomplete
                    value={CURRENCY_OPTIONS.find(
                      (item) => item.value === field.value
                    )}
                    options={CURRENCY_OPTIONS}
                    getOptionLabel={(option) => {
                      return t(option.label);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_event, value) => {
                      field.onChange(value.value as Currency);
                    }}
                    onBlur={field.onBlur}
                    disableClearable
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth>
              <UIFormLabel>{t('common:bank_branch')}:</UIFormLabel>
              <UIAutocomplete
                options={branches?.data ?? []}
                getOptionLabel={(option) => option.branchName}
                TextFieldProps={{
                  placeholder: t('common:not_selected'),
                }}
                onChange={(_event, value) => {
                  setValue('branchId', value?.unique ?? null);
                }}
              />
            </FormControl>
          </Stack>
        </Stack>
        <Stack direction='column' spacing={2} mt={3}>
          <Typography fontSize={17} fontWeight={600}>
            {t('general_agreement')}
          </Typography>

          <RadioGroup
            value={radioValue}
            onChange={(e) => setRadioValue(Number(e.target.value))}
          >
            {radioGroup.map((item) => {
              const isAgreementOptionBlocked =
                blockedAgreementCreation && item.id === 2;

              return (
                <Box
                  key={item.id}
                  sx={{
                    background: '#0B13700A',
                    px: '1.2rem',
                    borderRadius: '1.2rem',
                    mb: '1rem',
                    position: 'relative',
                    opacity: isAgreementOptionBlocked ? 0.7 : 1,
                  }}
                >
                  <FormControlLabel
                    value={item.id}
                    disabled={isAgreementOptionBlocked}
                    control={
                      <Radio
                        disableRipple
                        sx={{
                          color: '#02A8D8',
                          boxShadow: 'none',
                          '&:hover, &:active, &.Mui-focusVisible': {
                            backgroundColor: 'transparent',
                          },
                          '&.Mui-checked': {
                            color: '#02A8D8',
                          },
                          '& .MuiTouchRipple-root': {
                            display: 'none',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 24,
                            width: 24,
                            height: 24,
                            '& circle:first-of-type': {
                              fill: 'none',
                              stroke: '#02A8D8',
                              strokeWidth: 2,
                            },
                            '& circle:last-of-type': {
                              fill: 'none',
                              stroke: 'none',
                            },
                          },
                          '&.Mui-checked .MuiSvgIcon-root': {
                            color: '#02A8D8',
                            '& circle:first-of-type': {
                              stroke: '#02A8D8',
                            },
                            '& circle:last-of-type': {
                              fill: '#02A8D8',
                              stroke: '#02A8D8',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <>
                        <Typography
                          fontSize={14}
                          fontWeight={600}
                          color='#0D1D58'
                        >
                          {item.label}
                        </Typography>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: '50%',
                            transform: 'translate(50%, -50%)',
                          }}
                        >
                          {isAgreementOptionBlocked && (
                            <Stack direction='row' spacing={1}>
                              <AlertIcon />
                              <Typography
                                fontSize={13}
                                fontWeight={500}
                                color='#0D1D58'
                              >
                                {t('general_agreement_blocked_alert')}
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      </>
                    }
                  />
                  {radioValue === item.id && (
                    <Box
                      sx={{
                        p: item.id === 0 ? '0' : '0 2rem 2rem 3rem',
                        animation: 'fadeIn 0.5s ease-in-out',
                        '@keyframes fadeIn': {
                          from: {
                            opacity: 0,
                            transform: 'translateY(-10px)',
                          },
                          to: {
                            opacity: 1,
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                    >
                      {item.id === 1 && (
                        <Stack direction='row' spacing={2} maxWidth={'60%'}>
                          <FormControl fullWidth required>
                            <UIFormLabel>
                              {t('payment_document.document_number')}:
                            </UIFormLabel>
                            <FormTextInput
                              control={control}
                              name='generalAgreementDocumentNumber'
                              rules={{ required: true }}
                            />
                          </FormControl>
                          <FormControl fullWidth required>
                            <UIFormLabel>
                              {t('payment_document.document_date')}:
                            </UIFormLabel>
                            <FormDatePicker
                              control={control}
                              name='generalAgreementDocumentDate'
                              rules={{ required: true }}
                            />
                          </FormControl>
                        </Stack>
                      )}
                      {item.id === 2 && (
                        <Stack direction='column' spacing={2} mt={2}>
                          <Stack direction='row' spacing={2} maxWidth={'60%'}>
                            <FormControl>
                              <UIFormLabel>
                                {t('payment_document.document_number')}:
                              </UIFormLabel>
                              <FormTextInput
                                control={control}
                                name='generalAgreementDocumentNumber'
                                rules={{ required: true }}
                              />
                            </FormControl>
                            <FormControl fullWidth>
                              <UIFormLabel>
                                {t('payment_document.document_date')}:
                              </UIFormLabel>
                              <FormDatePicker
                                control={control}
                                name='generalAgreementDocumentDate'
                                rules={{ required: true }}
                              />
                            </FormControl>
                          </Stack>
                          <FactorAddDocument
                            label={t('general_agreement')}
                            value={generalAgreementFile}
                            documentType='general_agreement'
                            type='factor'
                            onChange={(file) => {
                              setGeneralAgreementFile(file);
                            }}
                            onDelete={() => {
                              setGeneralAgreementFile(null);
                            }}
                            onDeleteFile={() => {
                              setGeneralAgreementFile(null);
                            }}
                            isDisabledGenerateGeneralAgreement={
                              isGenerateLoading || isFieldsEmpty
                            }
                            onGenerateGeneralAgreement={
                              handleGenerateGeneralAgreement
                            }
                            isLoadingGenerateGeneralAgreement={
                              isLoadingGenerateGeneralAgreement
                            }
                          />
                        </Stack>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </RadioGroup>
          <Stack direction='row' spacing={2} mt={2.5} justifyContent='flex-end'>
            <Button
              color='inherit'
              onClick={() => {
                navigate(-1);
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              disabled={isCreateLoading}
              onClick={handleSubmit(handleSave)}
            >
              {radioValue === 0 ? t('save_and_add') : t('save_and_send')}
            </Button>
          </Stack>
        </Stack>
        <Divider sx={{ my: 2.5 }} />
        <SubjectInfoTabs tin={tin} defaultEnabled />
      </ComponentWithLoader>
    </CabinetWrapper>
  );
};
